import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPickupOptions as clientListPickupOptions,
  reservePickupLocation as clientReservePickupLocation,
  updatePickupPerson as clientUpdateOrderPickupPersonInformation,
} from 'typescript/clients/solidshop/bopis_client';
import { MUTATION_KEYS, QUERY_KEYS } from 'typescript/constants/react_query';
import { PickupOption } from 'typescript/types/api/pickup_options';
import { OrderPickupPerson } from 'typescript/types/api/solidshop';
import { CustomError } from 'typescript/utilities/custom_error';

const useListPickupOptions = ({ orderNumber }: { orderNumber: string }) => {
  const queryClient = useQueryClient();

  const {
    mutate: getPickupOptions,
    data: pickupOptions,
    reset: resetPickupOptions,
    ...rest
  } = useMutation({
    mutationFn: async ({ postalCode }: { postalCode: string }) => {
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.PICKUP_OPTIONS, { orderNumber }],
      });
      const response = await clientListPickupOptions({
        postalCode,
        orderNumber,
      });

      // only display the first three options
      // TODO: (SHOP-7677) move this to a limit param
      return response.slice(0, 3);
    },
    onError: (error) => {
      captureException(new CustomError(QUERY_KEYS.PICKUP_OPTIONS, error), {
        extra: { orderNumber },
      });

      return [];
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [QUERY_KEYS.PICKUP_OPTIONS, { orderNumber }],
        data,
      );
    },
  });

  return { pickupOptions, getPickupOptions, resetPickupOptions, ...rest };
};

const useReservePickupLocation = ({ orderNumber }: { orderNumber: string }) => {
  const queryClient = useQueryClient();

  const {
    mutate: reservePickupLocation,
    data: reservedPickupLocationId,
    ...rest
  } = useMutation({
    mutationKey: [MUTATION_KEYS.RESERVE_PICKUP_LOCATION, { orderNumber }],
    mutationFn: ({
      chosenPickupLocationId,
      searchPostalCode,
    }: {
      chosenPickupLocationId: string;
      searchPostalCode?: string;
    }) =>
      clientReservePickupLocation({
        chosenPickupLocationId,
        searchPostalCode,
        orderNumber,
      }),
    onMutate: async ({ chosenPickupLocationId }) => {
      // clear previous locations
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.PICKUP_OPTIONS, { orderNumber }],
      });

      const previousPickupOptions = queryClient.getQueryData([
        QUERY_KEYS.PICKUP_OPTIONS,
        { orderNumber },
      ]);

      queryClient.setQueryData(
        [QUERY_KEYS.PICKUP_OPTIONS, { orderNumber }],
        (old: PickupOption[]) =>
          old.map((pickupOption) => ({
            ...pickupOption,
            selected:
              pickupOption.location_id.toString() ===
              chosenPickupLocationId.toString(),
          })),
      );

      return { previousPickupOptions };
    },
    onError: (error, __, context) => {
      if (context?.previousPickupOptions) {
        queryClient.setQueryData(
          [QUERY_KEYS.PICKUP_OPTIONS, { orderNumber }],
          context.previousPickupOptions,
        );
      }

      captureException(
        new CustomError(MUTATION_KEYS.RESERVE_PICKUP_LOCATION, error),
        { extra: { orderNumber } },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PICKUP_OPTIONS, { orderNumber }],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
      });
    },
  });

  return { reservePickupLocation, reservedPickupLocationId, ...rest };
};

const useUpdatePickupPerson = ({ orderNumber }: { orderNumber: string }) => {
  const { mutate: updatePickupPerson, ...rest } = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_PICKUP_PERSON, { orderNumber }],
    mutationFn: ({
      pickupPersonInfo,
    }: {
      pickupPersonInfo: OrderPickupPerson;
    }) =>
      clientUpdateOrderPickupPersonInformation({
        pickupPersonInfo,
        orderNumber,
      }),
    onError: (error) =>
      captureException(
        new CustomError(MUTATION_KEYS.UPDATE_PICKUP_PERSON, error),
        { extra: { orderNumber } },
      ),
  });

  return { updatePickupPerson, ...rest };
};

export {
  useListPickupOptions,
  useReservePickupLocation,
  useUpdatePickupPerson,
};
